import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/action/authSlice";
import toast from "react-hot-toast";

const Navbar = () => {
  const cart = useSelector((s) => s.handleCart);
  const cartCount = cart?.length || 0;
  
  const { isAuthenticated, user, token } = useSelector((s) => s.auth);
  const dispatch = useDispatch();

  const [q, setQ] = useState("");

  // desktop categories dropdown
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef(null);

  // mobile/tablet bottom sheet categories
  const [catSheetOpen, setCatSheetOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Logged out successfully");
    navigate("/");
    setProfileOpen(false);
  };

  // close dropdown/sheet on route change
  useEffect(() => {
    setCatOpen(false);
    setCatSheetOpen(false);
    setProfileOpen(false);
  }, [location.pathname, location.search]);

  // Handle cross-domain logout signal from dashboard
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('logout') === 'true') {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://apistore.iqbaldeveloper.my.id";
      
      // Validate referrer to prevent Logout CSRF
      if (document.referrer && document.referrer.startsWith(backendUrl)) {
        dispatch(logout());
        toast.success("Logged out successfully");
      } else {
        console.warn("Logout signal ignored due to invalid referrer (CSRF Protection).");
      }
      
      // Clean up URL
      searchParams.delete('logout');
      const newSearch = searchParams.toString();
      navigate({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      }, { replace: true });
    }
  }, [location.search, location.pathname, dispatch, navigate]);

  // lock body scroll when mobile category sheet open
  useEffect(() => {
    if (!catSheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [catSheetOpen]);

  // close desktop category & profile on outside click / ESC
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === "Escape") {
        setCatOpen(false);
        setProfileOpen(false);
      }
    };
    const onClick = (e) => {
      if (catOpen && catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
      if (profileOpen && profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("mousedown", onClick);
    };
  }, [catOpen, profileOpen]);

  const navItems = useMemo(
    () => [
      { to: "/", label: "Home" },
      { to: "/product", label: "Produk" },
      { to: "/about", label: "Tentang" },
      { to: "/contact", label: "Kontak" },
    ],
    []
  );

  const categories = useMemo(
    () => [
      { to: "/product", label: "Semua" },
      { to: "/product?cat=men's clothing", label: "Templates" },
      { to: "/product?cat=women's clothing", label: "Ebooks" },
      { to: "/product?cat=jewelery", label: "Courses" },
      { to: "/product?cat=electronics", label: "Software" },
    ],
    []
  );

  const onSearch = (e) => {
    e.preventDefault();
    const v = q.trim();
    navigate(v ? `/product?search=${encodeURIComponent(v)}` : "/product");
  };

  const navLinkClass = ({ isActive }) =>
    [
      "relative px-3 py-2 text-sm font-semibold transition",
      "text-zinc-300 hover:text-white",
      isActive ? "text-white" : "",
    ].join(" ");

  const activeUnderline = ({ isActive }) =>
    isActive ? "absolute left-3 right-3 -bottom-1 h-[2px] rounded-full bg-white/90" : "hidden";

  // ====== icons ======
  const IconHome = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path
        d="M4 10.5 12 4l8 6.5V20a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 20v-9.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 21.5v-6.2c0-.7.6-1.3 1.3-1.3h2.4c.7 0 1.3.6 1.3 1.3v6.2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  const IconGrid = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path
        d="M4 4h7v7H4V4Zm9 0h7v7h-7V4ZM4 13h7v7H4v-7Zm9 0h7v7h-7v-7Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );

  const IconList = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const IconWallet = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 12h5v4h-5a2 2 0 0 1-2-2v-0a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const IconTag = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path
        d="M3 12V4h8l10 10-8 8L3 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M7.5 7.5h.01" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );

  const IconUser = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path d="M20 21a8 8 0 1 0-16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );

  const IconCart = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path d="M6 7h15l-1.5 9h-12L6 7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M6 7 5 4H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const IconSearch = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const IconDashboard = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="3" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="14" y="12" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="3" y="16" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="2" />
    </svg>
  );

  return (
    <>
      <header className="sticky top-0 z-50">
        {/* Utility top bar */}
        <div className="bg-zinc-950 text-zinc-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-2 text-xs sm:text-sm">
              <div className="truncate">
                <span className="font-semibold text-white">Instant download</span>{" "}
                <span className="opacity-60">•</span> Pembayaran aman <span className="opacity-60">•</span> Support via
                email/WA
              </div>

              <div className="hidden md:flex items-center gap-5 opacity-95">
                <NavLink to="/panduan-belanja" className="hover:text-white hover:underline">
                  Panduan
                </NavLink>
                <NavLink to="/help" className="hover:text-white hover:underline">
                  Bantuan
                </NavLink>
                <NavLink to="/about" className="hover:text-white hover:underline">
                  Tentang
                </NavLink>
                <NavLink to="/blog" className="hover:text-white hover:underline">
                  Blog
                </NavLink>
              </div>
            </div>
          </div>
        </div>

        {/* Main bar */}
        <nav className="border-b border-white/10 bg-zinc-950/90 backdrop-blur supports-[backdrop-filter]:bg-zinc-950/70">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between gap-3">
              {/* Brand */}
              <NavLink to="/" className="flex items-center gap-2 font-extrabold text-white">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15">
                  <IconCart className="h-5 w-5" />
                </span>
                <span className="text-base sm:text-lg tracking-tight">Hyperstore</span>
              </NavLink>

              {/* Desktop middle: Categories dropdown + Search */}
              <div className="hidden lg:flex flex-1 items-center gap-3 mx-2">
                {/* Categories dropdown */}
                <div className="relative" ref={catRef}>
                  <button
                    type="button"
                    onClick={() => setCatOpen((v) => !v)}
                    className={[
                      "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition",
                      "bg-white/10 text-white hover:bg-white/15 ring-1 ring-white/10",
                      catOpen ? "ring-2 ring-emerald-400/50" : "",
                    ].join(" ")}
                    aria-expanded={catOpen}
                    aria-controls="cat-menu"
                  >
                    Kategori
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>

                  {catOpen && (
                    <div
                      id="cat-menu"
                      className="absolute left-0 mt-2 w-72 overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/95 shadow-2xl backdrop-blur"
                    >
                      <div className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold text-zinc-400">Jelajahi kategori</div>
                        <div className="grid grid-cols-2 gap-2 p-2 pt-0">
                          {categories.map((c) => (
                            <NavLink
                              key={c.label}
                              to={c.to}
                              onClick={() => setCatOpen(false)}
                              className="rounded-xl bg-white/10 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-white/15 transition ring-1 ring-white/10"
                            >
                              {c.label}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Search */}
                <form onSubmit={onSearch} className="flex-1">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                      <IconSearch className="h-5 w-5" />
                    </span>
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Cari template, ebook, course..."
                      className="w-full rounded-2xl bg-white pl-10 pr-4 py-2 text-sm text-black placeholder:text-zinc-400 focus:outline-none"
                    />
                  </div>
                </form>
              </div>

              {/* Desktop right */}
              <div className="hidden lg:flex lg:items-center lg:gap-2">
                <div className="hidden xl:flex xl:items-center xl:gap-1">
                  {navItems.map((it) => (
                    <NavLink key={it.to} to={it.to} className={navLinkClass}>
                      {({ isActive }) => (
                        <span className="relative">
                          {it.label}
                          <span className={activeUnderline({ isActive })} />
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>

                {/* Cart */}
                <NavLink
                  to="/cart"
                  className="relative inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                  title="Cart"
                >
                  <IconCart className="h-6 w-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex min-w-5 justify-center rounded-full bg-emerald-400 px-1.5 py-0.5 text-[10px] font-extrabold text-black">
                      {cartCount}
                    </span>
                  )}
                </NavLink>

                {!isAuthenticated ? (
                  <>
                    <NavLink
                      to="/login"
                      className="rounded-xl px-3 py-2 text-sm font-semibold text-zinc-200 hover:text-white hover:bg-white/10 transition"
                    >
                      Masuk
                    </NavLink>

                    <NavLink
                      to="/register"
                      className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-extrabold text-white hover:bg-emerald-400 transition"
                    >
                      Daftar
                    </NavLink>
                  </>
                ) : (
                  <>
                    {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'seller' || user?.role === 'user' || user?.role === 'customer') && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL || "https://apistore.iqbaldeveloper.my.id"}${user?.role === 'seller' ? '/admin' : '/admin'}/auth/callback?ref=/app/dashboard&source=store#token=${token}`}
                        className="hidden md:inline-flex rounded-xl bg-indigo-500 px-4 py-2 text-sm font-extrabold text-white hover:bg-indigo-400 transition ml-2"
                      >
                        Dashboard
                      </a>
                    )}
                    <div className="relative ml-2" ref={profileRef}>
                      <button
                        onClick={() => setProfileOpen(!profileOpen)}
                        className="flex items-center gap-2 rounded-xl bg-white/5 pl-2 pr-3 py-1 ring-1 ring-white/10 hover:bg-white/10 transition"
                      >
                      <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center text-sm font-bold text-white uppercase selection:bg-transparent">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                      <span className="text-sm font-medium text-white max-w-[100px] truncate">
                        {user?.name || "User"}
                      </span>
                    </button>
                    
                    {profileOpen && (
                      <div className="absolute right-0 mt-2 w-48 rounded-xl bg-zinc-900 border border-zinc-800 shadow-xl overflow-hidden py-1">
                        <div className="px-4 py-3 border-b border-zinc-800">
                           <p className="text-sm text-white font-medium truncate">{user?.name}</p>
                           <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
                        </div>
                        {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'seller' || user?.role === 'user' || user?.role === 'customer') && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL || "https://apistore.iqbaldeveloper.my.id"}${user?.role === 'seller' ? '/admin' : '/admin'}/auth/callback?ref=/app/dashboard&source=store#token=${token}`} className="md:hidden block px-4 py-2 text-sm text-indigo-400 hover:bg-zinc-800 hover:text-indigo-300">
                             Dashboard
                          </a>
                        )}
                        <NavLink to="/profile" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white">
                           Profil Saya
                        </NavLink>
                        <NavLink to="/wallet" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white">
                           Dompet & Saldo
                        </NavLink>
                        <NavLink to="/orders" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white">
                           Pesanan Saya
                        </NavLink>
                        <NavLink to="/support" className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white">
                           Pusat Bantuan
                        </NavLink>
                        {user?.role === 'user' && (
                          <NavLink to="/buka-toko" className="block px-4 py-2 text-sm text-emerald-400 font-semibold hover:bg-zinc-800">
                             ⭐ Mulai Berjualan
                          </NavLink>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left block px-4 py-2 text-sm text-rose-400 hover:bg-zinc-800 hover:text-rose-300"
                        >
                          Keluar
                        </button>
                      </div>
                    )}
                  </div>
                  </>
                )}
              </div>

              {/* Mobile/Tablet right actions (visible < lg) */}
              <div className="lg:hidden flex items-center gap-2">
                {/* Categories */}
                <button
                  type="button"
                  onClick={() => setCatSheetOpen(true)}
                  className="inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                  aria-label="Kategori"
                  title="Kategori"
                >
                  <IconTag className="h-6 w-6" />
                </button>

                {/* Cart */}
                <NavLink
                  to="/cart"
                  className="relative inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                  title="Cart"
                >
                  <IconCart className="h-6 w-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex min-w-5 justify-center rounded-full bg-emerald-400 px-1.5 py-0.5 text-[10px] font-extrabold text-black">
                      {cartCount}
                    </span>
                  )}
                </NavLink>

                {/* Orders */}
                {isAuthenticated && (
                  <>
                    <NavLink
                      to="/wallet"
                      className="inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                      title="Dompet"
                      aria-label="Dompet"
                    >
                      <IconWallet className="h-6 w-6" />
                    </NavLink>
                    <NavLink
                      to="/orders"
                      className="inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                      title="Pesanan Saya"
                      aria-label="Pesanan Saya"
                    >
                      <IconList className="h-6 w-6" />
                    </NavLink>
                  </>
                )}

                {/* Login/Profile */}
                {!isAuthenticated ? (
                  <NavLink
                    to="/login"
                    className="inline-flex items-center justify-center rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                    title="Login"
                    aria-label="Login"
                  >
                    <IconUser className="h-6 w-6" />
                  </NavLink>
                ) : (
                  <NavLink
                    to="/profile"
                    className="flex items-center justify-center rounded-full bg-emerald-500 h-8 w-8 text-sm font-bold text-white uppercase ml-1 ring-2 ring-emerald-500/20 transition-all hover:bg-emerald-400"
                    title="Profil Saya"
                  >
                    {user?.name?.charAt(0) || "U"}
                  </NavLink>
                )}
              </div>
            </div>

            {/* Search bar for mobile/tablet (shows < lg) */}
            <div className="lg:hidden pb-3">
              <form onSubmit={onSearch}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                    <IconSearch className="h-5 w-5" />
                  </span>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cari template, ebook, course..."
                    className="w-full rounded-2xl bg-white pl-10 pr-4 py-2 text-sm text-black placeholder:text-zinc-400 focus:outline-none"
                  />
                </div>
              </form>
            </div>
          </div>
        </nav>

        {/* ======= Bottom Navigation (HP + Tablet) ======= */}
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-50">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 pb-[max(12px,env(safe-area-inset-bottom))]">
            <div className="rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur shadow-2xl">
              <div className={`grid ${isAuthenticated ? 'grid-cols-5' : 'grid-cols-4'}`}>
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    ["flex flex-col items-center justify-center py-3", "text-zinc-300 hover:text-white transition", isActive ? "text-white" : ""].join(
                      " "
                    )
                  }
                  aria-label="Home"
                  title="Home"
                >
                  <IconHome className="h-6 w-6" />
                </NavLink>

                <NavLink
                  to="/product"
                  className={({ isActive }) =>
                    ["flex flex-col items-center justify-center py-3", "text-zinc-300 hover:text-white transition", isActive ? "text-white" : ""].join(
                      " "
                    )
                  }
                  aria-label="Produk"
                  title="Produk"
                >
                  <IconGrid className="h-6 w-6" />
                </NavLink>

                <button
                  type="button"
                  onClick={() => setCatSheetOpen(true)}
                  className="flex flex-col items-center justify-center py-3 text-zinc-300 hover:text-white transition"
                  aria-label="Kategori"
                  title="Kategori"
                >
                  <IconTag className="h-6 w-6" />
                </button>

                {isAuthenticated ? (
                  <>
                    {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'seller' || user?.role === 'user' || user?.role === 'customer') && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL || "https://apistore.iqbaldeveloper.my.id"}${user?.role === 'seller' ? '/admin' : '/admin'}/auth/callback?ref=/app/dashboard&source=store#token=${token}`}
                        className="flex flex-col items-center justify-center py-3 text-zinc-300 hover:text-indigo-400 transition"
                        aria-label="Dashboard"
                        title="Dashboard"
                      >
                        <IconDashboard className="h-6 w-6" />
                      </a>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex flex-col items-center justify-center py-3 text-zinc-300 hover:text-rose-400 transition"
                      aria-label="Logout"
                      title="Logout"
                    >
                      <IconUser className="h-6 w-6" />
                    </button>
                  </>
                ) : (
                  <NavLink
                    to="/login"
                    className={({ isActive }) =>
                      ["flex flex-col items-center justify-center py-3", "text-zinc-300 hover:text-white transition", isActive ? "text-white" : ""].join(
                        " "
                      )
                    }
                    aria-label="Login"
                    title="Login"
                  >
                    <IconUser className="h-6 w-6" />
                  </NavLink>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile category sheet */}
        {catSheetOpen && (
          <>
            <button
              className="lg:hidden fixed inset-0 z-40 bg-black/50"
              onClick={() => setCatSheetOpen(false)}
              aria-label="Close categories overlay"
            />
            <div className="lg:hidden fixed inset-x-0 bottom-0 z-50">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 pb-[max(12px,env(safe-area-inset-bottom))]">
                <div className="rounded-3xl border border-white/10 bg-zinc-950/95 backdrop-blur shadow-2xl overflow-hidden max-h-[75vh]">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="text-sm font-extrabold text-white">Kategori</div>
                    <button
                      onClick={() => setCatSheetOpen(false)}
                      className="rounded-xl p-2 text-zinc-200 hover:bg-white/10 hover:text-white transition"
                      aria-label="Close categories"
                    >
                      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                        <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>

                  <div className="p-4 overflow-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {categories.map((c) => (
                        <NavLink
                          key={c.label}
                          to={c.to}
                          onClick={() => setCatSheetOpen(false)}
                          className="rounded-2xl bg-white/10 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-white/15 transition ring-1 ring-white/10"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>

                    <div className="mt-3 text-xs text-zinc-400">
                      Tip: Kamu bisa tambah kategori lain kapan saja dari array{" "}
                      <code className="text-zinc-200">categories</code>.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
};

export default Navbar;
